import {
  isDateRangeOperator,
  isRelativeDateRangeOperator,
  isStandardDateOperator,
} from '@doss/common-all'
import { capitalize, startCase } from 'lodash'
import { useMemo } from 'react'
import { Dropdown } from '~/@/components/inputs/selection/dropdown'
import { DataType } from '~/common/generated/apollo-operations'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { getGridFilterOperators } from '~/common/utils/grid-filters'
import { ActiveUser } from '~/components/settings/types'
import { TableColumnOption } from '~/components/shared/Table/TableColumnAutocomplete'
interface FilterOperatorSlectorProps {
  column: TableColumnOption | null
  disabled?: boolean
  value: string | null
  onChange: (value: string | null) => void
  activeUsers?: ActiveUser[]
  includeDuplicateOperator?: boolean
}
/**
 * Renders the dropdown select for filter operators based on the current selected table column
 */
export function FilterOperatorSelector({
  column,
  disabled,
  value,
  onChange,
  activeUsers,
  includeDuplicateOperator = false,
}: FilterOperatorSlectorProps) {
  const operators = useMemo(
    () => (column ? getGridFilterOperators(column, { activeUsers, includeDuplicateOperator }) : []),
    [column, activeUsers, includeDuplicateOperator],
  )
  const { t } = useTypedTranslation()
  const options = useMemo(() => {
    // Export summaries translate persisted FilterOperator values separately; this labels the
    // DataGrid operator strings used by the interactive filter panel.
    const getOperatorLabel = (operator: (typeof operators)[number]): string => {
      const label =
        operator.value === 'isDuplicate'
          ? t('table.filter_operator_is_duplicate')
          : operator.label || startCase(operator.value)
      return capitalize(label)
    }

    // For lookup columns, use resolvedSourceType to handle multi-level lookups
    const columnDataType =
      column?.type === DataType.LOOKUP ? column.resolvedSourceType : column?.type

    if (columnDataType === DataType.DATE || columnDataType === DataType.DATE_TIME) {
      const relativeOperators = operators.filter((operator) =>
        isRelativeDateRangeOperator(operator.value),
      )
      const rangeOperators = operators.filter((operator) => isDateRangeOperator(operator.value))
      const standardOperators = operators.filter((operator) =>
        isStandardDateOperator(operator.value),
      )
      const duplicateOperators = operators.filter((operator) => operator.value === 'isDuplicate')

      // Flatten all grouped options into a single array of Option objects
      return [
        ...relativeOperators.map((operator) => ({
          label: getOperatorLabel(operator),
          value: operator.value,
        })),
        ...rangeOperators.map((operator) => ({
          label: getOperatorLabel(operator),
          value: operator.value,
        })),
        ...standardOperators.map((operator) => ({
          label: getOperatorLabel(operator),
          value: operator.value,
        })),
        ...duplicateOperators.map((operator) => ({
          label: getOperatorLabel(operator),
          value: operator.value,
        })),
      ]
    }

    return operators.map((operator) => ({
      label: getOperatorLabel(operator),
      value: operator.value,
    }))
  }, [operators, column, t])

  return (
    <Dropdown
      className="h-9 w-24 text-xs"
      disabled={disabled}
      onChange={(value) => onChange(value ?? null)}
      options={options.flatMap((option) => {
        if ('options' in option) {
          return (option.options as { value: string; label: string }[]).map((option) => ({
            value: option.value,
            label: option.label,
          }))
        }
        return [{ value: option.value, label: option.label }]
      })}
      placeholder={t('table.filter_operator_selector.select_operator')}
      value={value ?? undefined}
      wrapperClassName="flex-1"
    />
  )
}
